import React from 'react';
import { Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { CFT150, FT151 } from '../../../services/pwt205Api';
import LookupField from '../../../components/LookupField';

interface Tab1Props {
  formData: Partial<CFT150>;
  attendees: Partial<FT151>[];
  isEditing: boolean;
  getField: (key: keyof CFT150) => unknown;
  setField: (key: keyof CFT150, value: unknown) => void;
  onAddAttendee: () => void;
  onDeleteAttendee: (idx: number) => void;
  onUpdateAttendee: (idx: number, key: keyof FT151, value: unknown) => void;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(
  ({
    formData,
    attendees,
    isEditing,
    getField,
    setField,
    onAddAttendee,
    onDeleteAttendee,
    onUpdateAttendee,
  }) => {
    const strField = (k: keyof CFT150) => String(getField(k) ?? '');

    return (
      <div className="pwt205-tab-content">
        {/* Top Info Row */}
        <div className="form-grid">
          <label>開始時間</label>
          <input
            value={strField('ft150_14')}
            onChange={(e) => {
              const v = e.target.value;
              setField('ft150_14', v);
            }}
            onBlur={(e) => {
              const v = e.target.value;
              if (v && !/^\d{2}:\d{2}:\d{2}$/.test(v)) {
                // Allow partial input but warn on blur
              }
            }}
            disabled={!isEditing}
            placeholder="HH:MM:SS"
            maxLength={8}
          />
          <label>結束時間</label>
          <input
            value={strField('ft150_15')}
            onChange={(e) => {
              const v = e.target.value;
              setField('ft150_15', v);
            }}
            onBlur={(e) => {
              const v = e.target.value;
              if (v && !/^\d{2}:\d{2}:\d{2}$/.test(v)) {
                // Allow partial input but warn on blur
              }
            }}
            disabled={!isEditing}
            placeholder="HH:MM:SS"
            maxLength={8}
          />
          <label>會議地點</label>
          <input
            value={strField('ft150_16')}
            onChange={(e) => setField('ft150_16', e.target.value)}
            disabled={!isEditing}
            maxLength={40}
          />
          <label>開會部門</label>
          <LookupField
            lookupType="department"
            value={strField('ft150_24')}
            onChange={(code) => setField('ft150_24', code)}
            disabled={!isEditing}
            style={{ width: 120 }}
            title="搜尋部門"
          />
          <label>人數</label>
          <input value={String(formData.ft150_18 ?? 0)} readOnly disabled />
        </div>

        {/* 說明 (editbox → TextArea) */}
        <div className="form-row">
          <label>說明</label>
          <textarea
            value={strField('ft150_12')}
            onChange={(e) => setField('ft150_12', e.target.value)}
            disabled={!isEditing}
            rows={3}
          />
        </div>

        {/* 與會人員 Grid */}
        <div className="section-header">
          <strong>與會人員</strong>
          {isEditing && (
            <Button size="small" icon={<PlusOutlined />} onClick={onAddAttendee}>
              明細新增
            </Button>
          )}
        </div>
        <table className="detail-table">
          <thead>
            <tr>
              <th>序號</th>
              <th>員工代號</th>
              <th>姓名</th>
              <th>部門代號</th>
              <th>部門名稱</th>
              <th>簽到時間</th>
              <th>簽退時間</th>
              {isEditing && <th>操作</th>}
            </tr>
          </thead>
          <tbody>
            {attendees.map((att, idx) => (
              <tr key={idx}>
                <td>{att.ft151_02 ?? idx + 1}</td>
                <td>
                  <input
                    value={att.ft151_11 ?? ''}
                    onChange={(e) => onUpdateAttendee(idx, 'ft151_11', e.target.value)}
                    disabled={!isEditing}
                    style={{ width: '80px' }}
                  />
                </td>
                <td>
                  <input
                    value={att.ft151_11a ?? ''}
                    onChange={(e) => onUpdateAttendee(idx, 'ft151_11a', e.target.value)}
                    disabled={!isEditing}
                    style={{ width: '80px' }}
                  />
                </td>
                <td>
                  <input
                    value={att.ft151_12 ?? ''}
                    onChange={(e) => onUpdateAttendee(idx, 'ft151_12', e.target.value)}
                    disabled={!isEditing}
                    style={{ width: '60px' }}
                  />
                </td>
                <td>
                  <input
                    value={att.ft151_12a ?? ''}
                    onChange={(e) => onUpdateAttendee(idx, 'ft151_12a', e.target.value)}
                    disabled={!isEditing}
                    style={{ width: '80px' }}
                  />
                </td>
                <td>{att.ft151_13 ?? ''}</td>
                <td>{att.ft151_14 ?? ''}</td>
                {isEditing && (
                  <td>
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => onDeleteAttendee(idx)}
                    />
                  </td>
                )}
              </tr>
            ))}
            {attendees.length === 0 && (
              <tr>
                <td colSpan={isEditing ? 8 : 7} style={{ textAlign: 'center', color: '#999' }}>
                  尚無與會人員
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  },
);

Tab1BasicInfo.displayName = 'Tab1BasicInfo';

export default Tab1BasicInfo;
